import { supabase, setUserContext, clearUserContext } from './supabase';
import { TelegramService } from './telegram';
import type { TelegramUser, DatabaseUser } from '../types';

export interface AuthResult {
  success: boolean;
  user?: DatabaseUser;
  error?: string;
  errorCode?: AuthErrorCode;
}

export enum AuthErrorCode {
  NOT_TELEGRAM_ENVIRONMENT = 'NOT_TELEGRAM_ENVIRONMENT',
  NO_USER_DATA = 'NO_USER_DATA',
  NO_INIT_DATA = 'NO_INIT_DATA',
  INVALID_INIT_DATA = 'INVALID_INIT_DATA',
  EXPIRED_INIT_DATA = 'EXPIRED_INIT_DATA',
  MALFORMED_USER_DATA = 'MALFORMED_USER_DATA',
  DATABASE_ERROR = 'DATABASE_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  CONTEXT_ERROR = 'CONTEXT_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

interface ValidationResult {
  isValid: boolean;
  error?: AuthErrorCode;
  userData?: TelegramUser;
}

export class TelegramAuthService {
  private static instance: TelegramAuthService;
  private telegramService: TelegramService;
  private currentUser: DatabaseUser | null = null;
  private authenticationInProgress = false;
  private maxRetries = 3;
  private retryDelay = 1000; // 1 second

  private constructor() {
    this.telegramService = TelegramService.getInstance();
  }

  public static getInstance(): TelegramAuthService {
    if (!TelegramAuthService.instance) {
      TelegramAuthService.instance = new TelegramAuthService();
    }
    return TelegramAuthService.instance;
  }

  /**
   * Authenticate user using Telegram initData with retry logic
   */
  public async authenticate(): Promise<AuthResult> {
    // Prevent concurrent authentication attempts
    if (this.authenticationInProgress) {
      return {
        success: false,
        error: 'Аутентификация уже выполняется',
        errorCode: AuthErrorCode.UNKNOWN_ERROR
      };
    }

    this.authenticationInProgress = true;

    try {
      return await this.authenticateWithRetry();
    } finally {
      this.authenticationInProgress = false;
    }
  }

  /**
   * Authenticate with retry logic for network failures
   */
  private async authenticateWithRetry(attempt = 1): Promise<AuthResult> {
    try {
      return await this.performAuthentication();
    } catch (error) {
      console.error(`Authentication attempt ${attempt} failed:`, error);
      
      if (attempt < this.maxRetries && this.isRetryableError(error)) {
        console.log(`Retrying authentication in ${this.retryDelay}ms...`);
        await this.delay(this.retryDelay);
        return this.authenticateWithRetry(attempt + 1);
      }

      return {
        success: false,
        error: 'Ошибка сети при аутентификации',
        errorCode: AuthErrorCode.NETWORK_ERROR
      };
    }
  }

  /**
   * Perform the actual authentication process
   */
  private async performAuthentication(): Promise<AuthResult> {
    try {
      // Check if running in Telegram environment
      if (!this.telegramService.isTelegramEnvironment()) {
        return {
          success: false,
          error: 'Приложение должно запускаться в Telegram',
          errorCode: AuthErrorCode.NOT_TELEGRAM_ENVIRONMENT
        };
      }

      // In development mode, create a mock user without database validation
      if (import.meta.env.VITE_DEV_MODE === 'true') {
        console.log('Running in development mode, using mock authentication');
        
        const telegramUser = this.telegramService.getTelegramUser();
        if (!telegramUser) {
          return {
            success: false,
            error: 'Не удалось получить данные пользователя Telegram',
            errorCode: AuthErrorCode.INVALID_INIT_DATA
          };
        }

        // Create mock database user
        const mockUser = {
          id: `mock-${telegramUser.id}`,
          telegram_id: telegramUser.id,
          first_name: telegramUser.first_name,
          last_name: telegramUser.last_name || null,
          username: telegramUser.username || null,
          language_code: telegramUser.language_code || 'ru',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        this.currentUser = mockUser;
        console.log('Mock authentication successful for user:', mockUser.telegram_id);
        
        return {
          success: true,
          user: mockUser
        };
      }

      // Get and validate initData
      const validationResult = await this.validateTelegramData();
      if (!validationResult.isValid || !validationResult.userData) {
        return {
          success: false,
          error: this.getErrorMessage(validationResult.error || AuthErrorCode.INVALID_INIT_DATA),
          errorCode: validationResult.error || AuthErrorCode.INVALID_INIT_DATA
        };
      }

      // Create or retrieve user from database
      const user = await this.createOrRetrieveUser(validationResult.userData);
      if (!user) {
        return {
          success: false,
          error: 'Не удалось создать или получить пользователя',
          errorCode: AuthErrorCode.DATABASE_ERROR
        };
      }

      // Set user context for RLS
      try {
        await setUserContext(user.telegram_id, user.id);
      } catch (error) {
        console.error('Error setting user context:', error);
        return {
          success: false,
          error: 'Ошибка настройки контекста пользователя',
          errorCode: AuthErrorCode.CONTEXT_ERROR
        };
      }

      // Store current user
      this.currentUser = user;

      console.log('Authentication successful for user:', user.telegram_id);
      return {
        success: true,
        user
      };
    } catch (error) {
      console.error('Authentication error:', error);
      return {
        success: false,
        error: 'Неизвестная ошибка аутентификации',
        errorCode: AuthErrorCode.UNKNOWN_ERROR
      };
    }
  }

  /**
   * Validate Telegram data and extract user information
   */
  private async validateTelegramData(): Promise<ValidationResult> {
    try {
      // Get Telegram user data
      const telegramUser = this.telegramService.getTelegramUser();
      if (!telegramUser) {
        return {
          isValid: false,
          error: AuthErrorCode.NO_USER_DATA
        };
      }

      // Get initData for validation
      const initData = this.telegramService.getInitData();
      if (!initData) {
        return {
          isValid: false,
          error: AuthErrorCode.NO_INIT_DATA
        };
      }

      // Validate initData structure and content
      const validationResult = await this.validateInitData(initData, telegramUser);
      if (!validationResult.isValid) {
        return validationResult;
      }

      return {
        isValid: true,
        userData: telegramUser
      };
    } catch (error) {
      console.error('Error validating Telegram data:', error);
      return {
        isValid: false,
        error: AuthErrorCode.UNKNOWN_ERROR
      };
    }
  }

  /**
   * Sign out current user
   */
  public async signOut(): Promise<void> {
    try {
      // Clear user context
      await clearUserContext();
      
      // Clear current user
      this.currentUser = null;
      
      console.log('User signed out successfully');
    } catch (error) {
      console.error('Error during sign out:', error);
    }
  }

  /**
   * Get current authenticated user
   */
  public getCurrentUser(): DatabaseUser | null {
    return this.currentUser;
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  /**
   * Validate Telegram initData with enhanced security checks
   * Note: In production, HMAC validation should be done server-side for security
   */
  private async validateInitData(initData: string, telegramUser: TelegramUser): Promise<ValidationResult> {
    try {
      if (!initData || initData.length === 0) {
        return {
          isValid: false,
          error: AuthErrorCode.NO_INIT_DATA
        };
      }

      // Parse initData parameters
      const params = new URLSearchParams(initData);
      const user = params.get('user');
      const authDate = params.get('auth_date');
      const hash = params.get('hash');
      
      if (!user || !authDate) {
        return {
          isValid: false,
          error: AuthErrorCode.INVALID_INIT_DATA
        };
      }

      // Check if auth_date is not too old (within 24 hours)
      const authTimestamp = parseInt(authDate);
      const currentTimestamp = Math.floor(Date.now() / 1000);
      const maxAge = 24 * 60 * 60; // 24 hours in seconds
      
      if (isNaN(authTimestamp) || currentTimestamp - authTimestamp > maxAge) {
        console.warn('InitData is too old or invalid auth_date');
        return {
          isValid: false,
          error: AuthErrorCode.EXPIRED_INIT_DATA
        };
      }

      // Parse and validate user data
      let userData: any;
      try {
        userData = JSON.parse(user);
        if (!userData.id || !userData.first_name) {
          return {
            isValid: false,
            error: AuthErrorCode.MALFORMED_USER_DATA
          };
        }
      } catch (error) {
        console.error('Invalid user data in initData:', error);
        return {
          isValid: false,
          error: AuthErrorCode.MALFORMED_USER_DATA
        };
      }

      // Verify that the parsed user data matches the Telegram user data
      if (userData.id !== telegramUser.id || userData.first_name !== telegramUser.first_name) {
        console.error('User data mismatch between initData and Telegram user');
        return {
          isValid: false,
          error: AuthErrorCode.INVALID_INIT_DATA
        };
      }

      // In development mode, skip HMAC validation
      // In production, this should validate the HMAC signature using bot token
      const isDevelopment = typeof window !== 'undefined' && 
                           (window.location.hostname === 'localhost' || 
                            window.location.hostname === '127.0.0.1');
      
      if (!isDevelopment && hash) {
        // TODO: Implement server-side HMAC validation
        console.warn('HMAC validation should be implemented server-side in production');
      }

      return {
        isValid: true
      };
    } catch (error) {
      console.error('Error validating initData:', error);
      return {
        isValid: false,
        error: AuthErrorCode.UNKNOWN_ERROR
      };
    }
  }

  /**
   * Create or retrieve user from database with enhanced error handling
   */
  private async createOrRetrieveUser(telegramUser: TelegramUser): Promise<DatabaseUser | null> {
    try {
      // First, try to find existing user
      const { data: existingUser, error: selectError } = await supabase
        .from('users')
        .select('*')
        .eq('telegram_id', telegramUser.id)
        .single();

      if (selectError && selectError.code !== 'PGRST116') {
        // PGRST116 is "not found" error, which is expected for new users
        console.error('Error finding user:', selectError);
        throw new Error(`Database error: ${selectError.message}`);
      }

      if (existingUser) {
        // Update existing user data in case it changed
        const updateData = {
          first_name: telegramUser.first_name,
          last_name: telegramUser.last_name || null,
          username: telegramUser.username || null,
          language_code: telegramUser.language_code || 'ru',
          updated_at: new Date().toISOString()
        };

        const { data: updatedUser, error: updateError } = await supabase
          .from('users')
          .update(updateData)
          .eq('telegram_id', telegramUser.id)
          .select()
          .single();

        if (updateError) {
          console.error('Error updating user:', updateError);
          // Return existing user if update fails - not critical
          console.warn('Using existing user data due to update failure');
          return existingUser;
        }

        console.log('User updated successfully:', updatedUser.id);
        return updatedUser;
      }

      // Create new user
      const insertData = {
        telegram_id: telegramUser.id,
        first_name: telegramUser.first_name,
        last_name: telegramUser.last_name || null,
        username: telegramUser.username || null,
        language_code: telegramUser.language_code || 'ru'
      };

      const { data: newUser, error: insertError } = await supabase
        .from('users')
        .insert(insertData)
        .select()
        .single();

      if (insertError) {
        console.error('Error creating user:', insertError);
        throw new Error(`Failed to create user: ${insertError.message}`);
      }

      console.log('New user created successfully:', newUser.id);
      return newUser;
    } catch (error) {
      console.error('Error in createOrRetrieveUser:', error);
      
      // If it's a network error, throw it to trigger retry logic
      if (this.isRetryableError(error)) {
        throw error;
      }
      
      return null;
    }
  }

  /**
   * Refresh user session (re-authenticate)
   */
  public async refreshSession(): Promise<AuthResult> {
    try {
      console.log('Refreshing user session...');
      
      // Clear current session
      await this.signOut();
      
      // Re-authenticate
      const result = await this.authenticate();
      
      if (result.success) {
        console.log('Session refreshed successfully');
      } else {
        console.error('Session refresh failed:', result.error);
      }
      
      return result;
    } catch (error) {
      console.error('Error refreshing session:', error);
      return {
        success: false,
        error: 'Не удалось обновить сессию',
        errorCode: AuthErrorCode.UNKNOWN_ERROR
      };
    }
  }

  /**
   * Handle authentication errors with user-friendly messages
   */
  public getErrorMessage(errorCode: AuthErrorCode): string {
    const errorMessages: Record<AuthErrorCode, string> = {
      [AuthErrorCode.NOT_TELEGRAM_ENVIRONMENT]: 'Это приложение работает только в Telegram',
      [AuthErrorCode.NO_USER_DATA]: 'Не удалось получить данные пользователя Telegram',
      [AuthErrorCode.NO_INIT_DATA]: 'Отсутствуют данные инициализации Telegram',
      [AuthErrorCode.INVALID_INIT_DATA]: 'Недействительные данные аутентификации',
      [AuthErrorCode.EXPIRED_INIT_DATA]: 'Данные аутентификации устарели, перезапустите приложение',
      [AuthErrorCode.MALFORMED_USER_DATA]: 'Некорректные данные пользователя',
      [AuthErrorCode.DATABASE_ERROR]: 'Ошибка базы данных при создании пользователя',
      [AuthErrorCode.NETWORK_ERROR]: 'Ошибка сети, проверьте подключение к интернету',
      [AuthErrorCode.CONTEXT_ERROR]: 'Ошибка настройки контекста пользователя',
      [AuthErrorCode.UNKNOWN_ERROR]: 'Неизвестная ошибка аутентификации'
    };

    return errorMessages[errorCode] || 'Неизвестная ошибка';
  }

  /**
   * Check if an error is retryable (network-related)
   */
  private isRetryableError(error: any): boolean {
    // Check for network-related errors that can be retried
    if (error?.code === 'NETWORK_ERROR' || error?.code === 'TIMEOUT') {
      return true;
    }
    
    // Check for specific error messages that indicate network issues
    const retryableMessages = [
      'network error',
      'timeout',
      'connection failed',
      'fetch failed',
      'network request failed'
    ];
    
    const errorMessage = error?.message?.toLowerCase() || '';
    return retryableMessages.some(msg => errorMessage.includes(msg));
  }

  /**
   * Utility method to add delay for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Validate user session and refresh if needed
   */
  public async validateSession(): Promise<boolean> {
    try {
      if (!this.currentUser) {
        return false;
      }

      // In development mode, always return true if we have a current user
      if (import.meta.env.VITE_DEV_MODE === 'true') {
        console.log('Development mode: skipping database session validation');
        return true;
      }

      // Check if we can still access user data (validates RLS context)
      const { data, error } = await supabase
        .from('users')
        .select('id')
        .eq('id', this.currentUser.id)
        .single();

      if (error || !data) {
        console.warn('Session validation failed, clearing current user');
        this.currentUser = null;
        await clearUserContext();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error validating session:', error);
      this.currentUser = null;
      await clearUserContext();
      return false;
    }
  }
}